import { ContentService, Employee, HasSubscriptionsToClose, HeadingComponent } from '@agingplan';
import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { environment } from 'src/environments/environment';

/**
 * Component that displays and animates the employees section
 * Handles the display and animation of employee profiles with GSAP animations
 */
@Component({
  selector: 'app-employees',
  templateUrl: './employees.component.html',
  styleUrls: ['./employees.component.scss'],
})
export class EmployeesComponent extends HasSubscriptionsToClose() implements AfterViewInit {
  /**
   * Observable stream of employee data
   */
  public employees$ = this.contentService.employees$;

  /**
   * Base path for static assets
   * @returns The Google bucket base URL for assets
   */
  public get assets_base_path(): string {
    return `${environment.google.bucket}`;
  }

  /**
   * Path specifically for employee images
   * @returns The complete path for employee image assets
   */
  public get assets_employee_path(): string {
    return `${this.assets_base_path}/assets/pages/employees/`;
  }

  /**
   * Reference to the container holding employee profiles
   * Used for animation targeting
   */
  @ViewChild('employeesContainer') employeesContainer?: ElementRef<HTMLDivElement>;

  /**
   * Reference to the heading component
   * Used for animating title elements
   */
  @ViewChild('heading') heading!: HeadingComponent;

  /**
   * Reference to the paragraph element
   * Used for animating descriptive text
   */
  @ViewChild('paragraph') paragraph!: ElementRef<HTMLParagraphElement>;

  constructor(
    private contentService: ContentService,
    private elementRef: ElementRef<HTMLDivElement>,
  ) {
    super();

    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);
  }

  /**
   * Initializes the component after view initialization
   * Sets up employee data subscription and triggers animations
   */
  ngAfterViewInit(): void {
    // Subscribe to employee data and trigger animation when available
    this.subscribeTo(
      this.employees$.subscribe((employees) => {
        if (employees && employees.length > 0) {
          // Use requestAnimationFrame instead of setTimeout for better performance
          requestAnimationFrame(() => {
            this.animation(employees);
          });
        }
      }),
    );
  }

  /**
   * Sets up and executes the GSAP animation sequence for employee profiles
   * @param employees - Array of employee data to verify animation targets
   */
  public animation(employees: Employee[]): void {
    if (this.employeesContainer?.nativeElement && this.elementRef?.nativeElement) {
      const profiles = this.employeesContainer.nativeElement.querySelectorAll('.col');

      // Only proceed if we have the correct number of profile elements
      if (profiles.length == employees.length) {
        // Create a GSAP timeline with default settings
        const timeline = gsap.timeline({
          defaults: {
            duration: 0.6,
          },
          scrollTrigger: {
            trigger: this.elementRef?.nativeElement.querySelector('.container'),
            start: 'top center',
            end: 'bottom center',
            toggleActions: 'play none none reverse', // Play on enter, reverse on leave
          },
        });

        // Sequence of animations
        timeline
          // Animate heading elements
          .fromTo(this.heading.h2!.nativeElement, { opacity: 0, y: 75 }, { y: 0, opacity: 1 })
          .fromTo(this.heading.h1!.nativeElement, { opacity: 0, y: 75 }, { y: 0, opacity: 1 })
          // Animate paragraph
          .fromTo(this.paragraph.nativeElement, { y: 75 }, { y: 0, opacity: 1 })
          // Stagger animate employee profiles
          .to(profiles, { stagger: 0.8, opacity: 1 });
      }
    }
  }
}
